import React, { FC } from 'react';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Tooltip,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client/react';
import { getInvoiceBatchTemplate } from 'apollo/templates/invoice-batches';
import { useParams, useNavigate } from 'react-router-dom';
import { formatCurrency } from 'utils/format';
import DownloadExportButton from 'components/shared/download-export-button/download-export-button.component';
import BackdropLoader from 'components/shared/backdrop-loader/backdrop-loader.component';
import { useInvoiceBatchExport } from 'hooks/useInvoiceBatchExport';
import { BATCH_STATUS_COLORS, ChipColor, resolveBatchDisplayStatus } from '../invoice-batches/invoice-batch-status';
import {
    ACCOUNTING_PROVIDER_LABEL,
    AccountingProvider,
} from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';

type BatchInvoice = {
    _id: string;
    invoiceNumber: string;
    status: string;
    total: { currency: string; totalIncVat: number };
    deliveryAddress: { name: string };
    dentist?: { name: { firstName: string; lastName: string } };
    processingErrorMessage?: string;
    orders: string[];
};

type FailedInvoice = {
    /** Missing when Fortnox/Visma rejected the invoice before it was saved. */
    invoiceId?: string | null;
    invoiceNumber?: string;
    clinicName?: string;
    errorMessage: string;
    provider?: AccountingProvider | null;
};

/** "Fortnox: Kunden är inte aktiv. (2003259)" for accounting rejections, the stored message otherwise. */
const failedInvoiceMessage = (failed: FailedInvoice): string =>
    failed.provider
        ? `${ACCOUNTING_PROVIDER_LABEL[failed.provider] ?? failed.provider}: ${failed.errorMessage}`
        : failed.errorMessage;

type InvoiceBatchData = {
    _id: string;
    status: string;
    invoiceCount: number;
    customerCount: number;
    totalOrderCount: number;
    createdAt: number;
    invoices: BatchInvoice[];
    failedInvoices: FailedInvoice[];
};

const INVOICE_STATUS_COLORS: Record<string, ChipColor> = {
    CREATED: 'success',
    SENT: 'success',
    ERROR: 'error',
    // Delivered in Fortnox, delivery-note attachment failed; needs a human, not a retry (DD-3504).
    EXTERNAL_INVOICE_ERROR: 'warning',
    PROCESSING: 'info',
    SCHEDULED: 'warning',
};

const buildDeliveryClinicLabel = (invoice: BatchInvoice): string => {
    const clinicName = invoice.deliveryAddress?.name;
    if (!clinicName) return '—';
    const firstName = invoice.dentist?.name?.firstName;
    if (!firstName) return clinicName;
    return `${clinicName} - ${firstName} ${invoice.dentist!.name.lastName}`;
};

const InvoiceBatchDetail: FC = () => {
    const { t: translate } = useTranslation(['invoices', 'common', 'users']);
    const { batchId } = useParams<{ batchId: string }>();
    const navigate = useNavigate();
    // The backend builds the workbook in the background; the hook downloads it on the socket event.
    const { requestExport: exportBatchOrders, isExporting } = useInvoiceBatchExport({ batchId });

    const { data, loading } = useQuery(getInvoiceBatchTemplate, {
        variables: { id: batchId },
        fetchPolicy: 'network-only',
        pollInterval: 1000 * 15,
        skip: !batchId,
    });

    const batch: InvoiceBatchData | undefined = data?.invoiceBatch;

    if (loading && !batch) {
        return (
            <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (!batch) {
        return null;
    }

    const displayStatus = resolveBatchDisplayStatus(batch);
    const hasErrors = displayStatus === 'ERROR';
    // While invoices are still processing the invoiced order set is not final yet. Gate on
    // the same derived status the chip shows: the stored batch status may lag behind it.
    const canExportOrders = displayStatus !== 'PROCESSING' && batch.invoices.length > 0;

    // Collect all error details for the alert — from ERROR invoices (before finalization)
    // and from failedInvoices not already covered by batch.invoices (pre-async failures)
    const failedInvoiceIds = new Set(batch.failedInvoices.map((fi) => fi.invoiceId));

    type ErrorDetail = { name: string; message: string };
    const errorDetails: ErrorDetail[] = [
        ...batch.invoices
            .filter((invoice) => invoice.status === 'ERROR')
            .map((invoice) => ({
                name: invoice.deliveryAddress?.name ?? '—',
                message:
                    invoice.processingErrorMessage ??
                    batch.failedInvoices.find((fi) => fi.invoiceId === invoice._id)?.errorMessage ??
                    '—',
            })),
        ...batch.failedInvoices
            .filter((fi) => !batch.invoices.some((inv) => inv._id === fi.invoiceId))
            .map((fi) => ({
                name: fi.clinicName ?? '—',
                message: failedInvoiceMessage(fi),
            })),
    ];

    return (
        <Box>
            <Box mb={3} display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <Typography variant="h6">{translate('invoices:INVOICE_BATCH')}</Typography>
                <Chip
                    label={translate(`invoices:BATCH_STATUS_${displayStatus}`)}
                    color={BATCH_STATUS_COLORS[displayStatus] ?? 'default'}
                    size="small"
                />
                <Box ml="auto">
                    <DownloadExportButton
                        label={translate('invoices:EXPORT_BATCH_ORDERS_EXCEL')}
                        isLoading={isExporting}
                        disabled={!canExportOrders}
                        onDownload={exportBatchOrders}
                    />
                </Box>
            </Box>
            <BackdropLoader isOpen={isExporting} message={translate('users:CREATING_EXPORT_WAIT')} />

            {hasErrors && (
                <Box mb={3} display="flex" flexDirection="column" gap={1}>
                    <Alert severity="error">
                        {errorDetails.map((errorDetail, index) => (
                            <Box key={index}>
                                {errorDetail.name}: {errorDetail.message}
                            </Box>
                        ))}
                    </Alert>
                    <Alert severity="info">{translate('invoices:BATCH_RETRY_INFO')}</Alert>
                </Box>
            )}

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{translate('invoices:INVOICE_NUMBER')}</TableCell>
                            <TableCell>{translate('invoices:CUSTOMER')}</TableCell>
                            <TableCell>{translate('invoices:DELIVERY_CLINIC')}</TableCell>
                            <TableCell>{translate('invoices:BATCH_STATUS')}</TableCell>
                            <TableCell align="right">{translate('invoices:TOTAL_AMOUNT')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {batch.invoices.map((invoice) => {
                            const isError = invoice.status === 'ERROR';
                            const isClickable = true;
                            const errorMessage =
                                invoice.processingErrorMessage ??
                                batch.failedInvoices.find((fi) => fi.invoiceId === invoice._id)?.errorMessage;
                            return (
                                <TableRow
                                    key={invoice._id}
                                    hover={!isError && isClickable}
                                    sx={{
                                        backgroundColor: isError ? 'error.light' : undefined,
                                        cursor: isClickable ? 'pointer' : 'default',
                                    }}
                                    onClick={() => {
                                        if (isClickable) {
                                            navigate(`/invoices/${invoice._id}/summary`);
                                        }
                                    }}
                                >
                                    <TableCell>{invoice.invoiceNumber ?? '—'}</TableCell>
                                    <TableCell>{invoice.deliveryAddress?.name ?? '—'}</TableCell>
                                    <TableCell>{buildDeliveryClinicLabel(invoice)}</TableCell>
                                    <TableCell>
                                        <Tooltip title={isError ? errorMessage ?? '' : ''}>
                                            <Chip
                                                label={translate(`invoices:INVOICE_STATUS_${invoice.status}`)}
                                                color={INVOICE_STATUS_COLORS[invoice.status] ?? 'default'}
                                                size="small"
                                            />
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="right">
                                        {formatCurrency(invoice.total?.totalIncVat, invoice.total?.currency)}
                                    </TableCell>
                                </TableRow>
                            );
                        })}

                        {batch.failedInvoices
                            .filter((fi) => !batch.invoices.some((inv) => inv._id === fi.invoiceId))
                            .map((fi, index) => (
                                <TableRow
                                    key={fi.invoiceId ?? `rejected-${index}`}
                                    sx={{ backgroundColor: 'error.light', cursor: 'default' }}
                                >
                                    <TableCell>{fi.invoiceNumber ?? '—'}</TableCell>
                                    <TableCell>{fi.clinicName ?? '—'}</TableCell>
                                    <TableCell>{fi.clinicName ?? '—'}</TableCell>
                                    <TableCell>
                                        <Tooltip title={failedInvoiceMessage(fi)}>
                                            <Chip
                                                label={translate('invoices:INVOICE_STATUS_ERROR')}
                                                color="error"
                                                size="small"
                                            />
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="right">—</TableCell>
                                </TableRow>
                            ))}

                        {batch.invoices.length === 0 && batch.failedInvoices.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5}>
                                    <Typography variant="body2" color="text.secondary" align="center">
                                        {translate('invoices:NO_INVOICES')}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default InvoiceBatchDetail;
